/**
 * Changes the platform admin makes to a business type (FR-ADM): which types onboarding offers and
 * what a new business of that type starts with. Anything left out keeps the code's pack.
 */
import { z } from "zod";
import { MODULES, type ModuleFlags, type PackDef } from "./types";

const text = (max: number) => z.string().trim().min(1).max(max);
const localized = (max: number) => z.object({ en: text(max), bn: text(max) }).strict();

const unitSchema = z
  .object({
    code: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_]{0,15}$/, "Lower-case letters, digits and _"),
    name: localized(30),
    fractional: z.boolean(),
  })
  .strict();

const customFieldSchema = z
  .object({
    key: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_]{0,31}$/, "Lower-case letters, digits and _"),
    entity: z.enum(["item", "customer", "supplier"]),
    type: z.enum(["text", "number", "date", "select", "boolean"]),
    label: localized(40),
    required: z.boolean(),
    searchable: z.boolean(),
    options: z
      .array(z.object({ value: text(40), label: localized(40) }).strict())
      .max(50)
      .optional(),
  })
  .strict();

export const packOverrideSchema = z
  .object({
    available: z.boolean(),
    name: localized(60),
    description: localized(200),
    modules: z.partialRecord(z.enum(MODULES), z.boolean()),
    baseUnits: z.array(unitSchema).min(1).max(40),
    unitConversions: z
      .array(
        z
          .object({ from: text(16), to: text(16), factor: z.number().int().min(2).max(100_000) })
          .strict(),
      )
      .max(40),
    categories: z.array(localized(60)).max(100),
    customFields: z.array(customFieldSchema).max(30),
    priceTiers: z.array(localized(40)).max(10),
    variantPresets: z
      .array(z.object({ name: localized(30), values: z.array(text(30)).max(30) }).strict())
      .max(3),
    defaultWarrantyMonths: z.number().int().min(0).max(120),
    nearExpiryDays: z.number().int().min(1).max(730),
  })
  .partial()
  .strict()
  .superRefine((v, ctx) => {
    const codes = new Set((v.baseUnits ?? []).map((u) => u.code));
    if (v.baseUnits && codes.size !== v.baseUnits.length)
      ctx.addIssue({
        code: "custom",
        path: ["baseUnits"],
        message: "Two units have the same code",
      });
    const keys = (v.customFields ?? []).map((f) => `${f.entity}:${f.key}`);
    if (new Set(keys).size !== keys.length)
      ctx.addIssue({
        code: "custom",
        path: ["customFields"],
        message: "Two fields have the same key",
      });
    for (const [i, f] of (v.customFields ?? []).entries())
      if (f.type === "select" && !f.options?.length)
        ctx.addIssue({
          code: "custom",
          path: ["customFields", i, "options"],
          message: "A list needs options",
        });
  });
export type PackOverride = z.infer<typeof packOverrideSchema>;

export const platformPacksSchema = z.record(
  z.string().regex(/^[a-z0-9_-]{2,32}$/),
  packOverrideSchema,
);
export type PlatformPacks = z.infer<typeof platformPacksSchema>;

/** The pack as onboarding and new businesses see it, after the admin's changes. */
export function effectivePack(pack: PackDef, override?: PackOverride | null): PackDef {
  if (!override) return pack;
  const modules = { ...pack.modules, ...override.modules } as ModuleFlags;
  const baseUnits = override.baseUnits ?? pack.baseUnits;
  const codes = new Set(baseUnits.map((u) => u.code));
  return {
    ...pack,
    available: override.available ?? pack.available,
    name: override.name ?? pack.name,
    description: override.description ?? pack.description,
    modules,
    baseUnits,
    // A conversion between units the type no longer has would fail when a business is set up.
    unitConversions: (override.unitConversions ?? pack.unitConversions).filter(
      (c) => codes.has(c.from) && codes.has(c.to),
    ),
    categories: override.categories ?? pack.categories,
    customFields: override.customFields ?? pack.customFields,
    priceTiers: override.priceTiers ?? pack.priceTiers,
    variantPresets: override.variantPresets ?? pack.variantPresets,
    defaultWarrantyMonths: override.defaultWarrantyMonths ?? pack.defaultWarrantyMonths,
    nearExpiryDays: override.nearExpiryDays ?? pack.nearExpiryDays,
  };
}
